import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./stallDetails.css";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import axios from "axios";

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "" // Uses proxy in development
    : "https://krushiapp.dindoripranit.org";

const StallDetails = () => {
  const { id } = useParams(); // This is stallNo like "A-101"
  const [stall, setStall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [hideButtons, setHideButtons] = useState(false);
  const hasFetched = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id || hasFetched.current) return;
    hasFetched.current = true;

    const fetchStall = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(`${API_BASE_URL}/api/v1/stall/list`);
        const stalls = response.data?.value;

        if (!Array.isArray(stalls)) {
          throw new Error("Invalid data format from server");
        }

        // Find stall by stallNo (e.g., "A-101", "B-205")
        const foundStall = stalls.find(
          (s) => String(s.stallNo).trim() === String(id).trim()
        );

        if (!foundStall) {
          throw new Error(`Stall with number "${id}" not found`);
        }

        // Build proper logo URL
        const logoUrl = foundStall.logoPhoto
          ? foundStall.logoPhoto.startsWith("http")
            ? foundStall.logoPhoto
            : `https://krushiapp.dindoripranit.org/uploads/${foundStall.logoPhoto}`
          : null;

        setStall({
          stallNo: foundStall.stallNo,
          stallTitle: foundStall.title || "Untitled Stall",
          owner: foundStall.stallOwner || "N/A",
          description: foundStall.description || "No description available.",
          address: foundStall.address || "No address available.",
          contact: foundStall.contactNo || "N/A",
          website: foundStall.website || null,
          logo: logoUrl,
        });
      } catch (err) {
        console.error("Failed to load stall:", err);
        setError(err.message || "Failed to load stall details");
      } finally {
        setLoading(false);
      }
    };

    fetchStall();
  }, [id]);

  const handleCancel = () => {
    navigate("/");
  };

  const downloadPage = () => {
    setHideButtons(true);

    setTimeout(() => {
      const element = document.querySelector(".invoice-box");
      if (!element) {
        setHideButtons(false);
        return;
      }

      html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      })
        .then((canvas) => {
          const link = document.createElement("a");
          link.download = `Stall_${stall.stallNo}_${stall.stallTitle}.png`.replace(/[^a-z0-9]/gi, "_");
          link.href = canvas.toDataURL("image/png");
          link.click();
          setIsDownloaded(true);
        })
        .catch((err) => {
          console.error("Download failed:", err);
          alert("Failed to download image");
        })
        .finally(() => {
          setHideButtons(false);
        });
    }, 200);
  };

  // Loading State
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px", fontSize: "18px" }}>
        Loading stall details...
      </div>
    );
  }

  // Error State
  if (error || !stall) {
    return (
      <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
        <h3>Error</h3>
        <p>{error || "Stall not found!"}</p>
        {/* <button onClick={() => navigate("/")} style={{ padding: "10px 20px", marginTop: "20px" }}>
          Back to List
        </button> */}
      </div>
    );
  }

  return (
    <div className="invoice-container">
      <div id="invoice-POS" className="invoice-box">
        {/* Header Logo */}
        <center id="top">
          <div className="logo">
           <img
            src="/assets/images/krushi_mahostav_logo_12595.png"
            alt="Krushi Mahostav"
            style={{ width: "16.125rem", marginTop: "11px" }}
          />
          </div>
        </center>

        {/* Stall Info */}
        <div
          id="mid"
          className="invoice-mid"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: "15px",
            padding: "10px",
            flexWrap: "wrap",
          }}
        >
          {/* Stall Logo */}
          <img
            src={stall.logo || "/assets/images/no-image.png"}
            alt={stall.stallTitle}
            onError={(e) => (e.target.src = "/assets/images/no-image.png")}
            style={{
              width: "70px",
              height: "70px",
              borderRadius: "8px",
              objectFit: "contain",
              border: "2px solid #ddd",
              backgroundColor: "#fff",
            }}
          />

          {/* Stall Title */}
          <h2
            style={{
              color: "#2E348E",
              fontSize: "20px",
              margin: "0",
              fontWeight: "bold",
              textTransform: "capitalize",
            }}
          >
            {stall.stallTitle}
          </h2>
        </div>

        {/* Description */}
        <div
          className="description-box"
          style={{ backgroundColor: "#f8f9fa", padding: "12px", margin: "15px 0", borderRadius: "8px" }}
        >
          <h4 style={{ margin: "0 0 8px 0", color: "#333" }}>Description:</h4>
          <p style={{ margin: 0, lineHeight: "1.5" }}>{stall.description}</p>
        </div>

        {/* Address */}
        <div
          className="address-box"
          style={{ backgroundColor: "#f0f0f0", padding: "12px", margin: "15px 0", borderRadius: "8px" }}
        >
          <h4 style={{ margin: "0 0 8px 0", color: "#333" }}>Address:</h4>
          <p style={{ margin: 0 }}>{stall.address}</p>
        </div>

        {/* Contact & Website */}
        <div
          className="contact-box"
          style={{ backgroundColor: "#e8f5e8", padding: "12px", margin: "15px 0", borderRadius: "8px" }}
        >
          <h4 style={{ margin: "0 0 8px 0", color: "#333" }}>Contact:</h4>
          <p style={{ margin: "0 0 10px 0", fontWeight: "bold" }}>{stall.contact}</p>

          <h4 style={{ margin: "10px 0 8px 0", color: "#333" }}>Website:</h4>
          {stall.website ? (
            <p style={{ margin: 0 }}>
              <a
                href={stall.website.startsWith("http") ? stall.website : `https://${stall.website}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1a0dab", textDecoration: "underline" }}
              >
                {stall.website}
              </a>
            </p>
          ) : (
            <p style={{ margin: 0, color: "#666" }}>N/A</p>
          )}
        </div>

        {/* Footer Logo */}
        <div id="legalcopy" className="invoice-legalcopy" style={{ textAlign: "center", marginTop: "20px" }}>
          <img
            style={{ width: "21.125rem", borderRadius: "10px" }}
            src="/assets/images/GAF.jpg"
            alt="GAF Foundation"
          />
        </div>

        {/* Action Buttons */}
        {!isDownloaded && !hideButtons && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "15px",
              marginTop: "25px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={downloadPage}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                testalign: "center",
                padding: "12px 20px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              <DownloadIcon />
              Download as Image
            </button>

            {/* <button
              onClick={handleCancel}
              style={{
                padding: "12px 20px",
                backgroundColor: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Back to List
            </button> */}
          </div>
        )}

        {isDownloaded && (
          <div style={{ textAlign: "center", marginTop: "20px", color: "green", fontWeight: "bold" }}>
            Image downloaded successfully!
          </div>
        )}
      </div>
    </div>
  );
};

export default StallDetails;